import React, { useState, useEffect } from "react";
import { NavLink, useNavigate, useLocation } from "react-router-dom";
import { withTheme } from "../theming";
import { userPool } from "../awsConfig";
import { useResponsive } from "../contexts/ResponsiveContext";
import { clearAuthState } from "../utils/authUtils";

const Header = ({ isAuthenticated, setIsAuthenticated, theme }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [startupName, setStartupName] = useState("");
  const navigate = useNavigate();
  const location = useLocation();
  const { isMobile, isPhoneLandscape } = useResponsive();
  const showHamburger = isMobile || isPhoneLandscape;

  // Check if we're on a public/auth page where menu items should be hidden
  const isAuthPage =
    location.pathname === "/login" ||
    location.pathname === "/register" ||
    location.pathname === "/confirm-signup";

  // Only show menu items if authenticated AND not on auth pages
  const shouldShowMenuItems = isAuthenticated && !isAuthPage;

  useEffect(() => {
    // Get startup name from localStorage
    const storedStartupName = localStorage.getItem("STARTUP");
    if (storedStartupName) {
      setStartupName(storedStartupName);
    }
  }, []);

  const handleLogout = () => {
    const cognitoUser = userPool.getCurrentUser();
    if (cognitoUser) {
      cognitoUser.signOut();
    }

    // Close the menu first
    setIsOpen(false);

    // Clear all authentication state (this also closes all modals)
    clearAuthState();
    setIsAuthenticated(false);

    // Force navigation to login page (replace prevents back button)
    window.location.replace("/login");
  };

  // Function to handle menu item click
  const handleMenuItemClick = () => {
    setIsOpen(false);
  };

  // X20 Research menu — keep tight. Order is left-to-right in the header.
  // Unauth visitors see /features/<slug> marketing pages; auth'd users go to the app paths.
  const publicMenuLinks = [
    { name: "Video History", path: "/features/video-history" },
    { name: "Assets Studio", path: "/features/assets-studio" },
    { name: "Reporter Agent", path: "/features/reporter-agent" },
    { name: "Research Deck", path: "/features/research-deck" },
    { name: "Regression Snapshots", path: "/features/regression-snapshots" },
    { name: "Architecture Diagrams", path: "/features/architecture-diagrams" },
    { name: "Your Brand", path: "/features/your-brand" },
    { name: "Help", path: "/features/help" },
  ];

  const menuLinks = [
    { name: "Video History", path: "/content-feed" },
    { name: "Assets Studio", path: "/assets-studio-v2" },
    { name: "Reporter Agent", path: "/reporter-agent" },
    { name: "Research Deck", path: "/research-deck" },
    { name: "Regression Snapshots", path: "/regression-snapshots" },
    { name: "Architecture Diagrams", path: "/architecture-diagrams" },
    { name: "Your Brand", path: "/brand-management-studio" },
    { name: "Help", path: "/help" },
  ];

  return (
    <header
      style={{
        backgroundColor: theme.backgroundColor || "#333",
        color: theme.textColor || "#FFF",
        position: "sticky",
        top: 0,
        zIndex: 1000,
      }}
      className="p-4"
    >
      <div className="mx-auto flex justify-between items-center pr-10 pl-5">
        {/* Logo */}
        <div className="flex-shrink-0 mr-8">
          <NavLink
            to="/"
            className={({ isActive }) => {
              const sizeCls = showHamburger ? "text-[135%]" : "text-[170%]";
              return isActive
                ? `font-bold ${sizeCls} hover:opacity-90 focus-visible:opacity-90`
                : `${sizeCls} font-bold hover:opacity-90 focus:opacity-90`;
            }}
            style={{ color: theme.accentColor || "#FFA72A" }}
          >
            X20 Research
          </NavLink>
        </div>

        {/* Hamburger Menu Icon */}
        <div className={showHamburger ? "block" : "hidden"}>
          <button
            onClick={() => setIsOpen(!isOpen)}
            className="focus:outline-none"
            style={{
              backgroundColor: theme.backgroundColor || "#333",
              color: theme.accentColor || "#FFF",
            }}
          >
            <svg
              className="w-6 h-6"
              fill="none"
              stroke="currentColor"
              viewBox="0 0 24 24"
              xmlns="http://www.w3.org/2000/svg"
            >
              <path
                strokeLinecap="round"
                strokeLinejoin="round"
                strokeWidth="2"
                d="M4 6h16M4 12h16M4 18h16"
              ></path>
            </svg>
          </button>
        </div>

        {/* Desktop Menu */}
        <nav
          className={showHamburger ? "hidden" : "flex space-x-4 items-center"}
        >
          {shouldShowMenuItems ? (
            <>
              {menuLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={({ isActive }) =>
                    isActive
                      ? "font-bold text-[120%] hover:opacity-90 focus-visible:opacity-90"
                      : "text-[120%] hover:opacity-90 focus:opacity-90"
                  }
                  style={{
                    color: theme.accentColor || "#FFA72A",
                  }}
                >
                  {link.name}
                </NavLink>
              ))}
              <NavLink
                to="#"
                onClick={handleLogout}
                className={({ isActive }) =>
                  isActive
                    ? "font-bold text-[120%] hover:opacity-90 focus-visible:opacity-90"
                    : "text-[120%] hover:opacity-90 focus:opacity-90"
                }
                style={{
                  color: theme.accentColor || "#FFA72A",
                }}
              >
                Logout
              </NavLink>
            </>
          ) : (
            <>
              {publicMenuLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  className={({ isActive }) =>
                    isActive
                      ? "font-bold text-[120%] hover:opacity-90 focus-visible:opacity-90"
                      : "text-[120%] hover:opacity-90 focus:opacity-90"
                  }
                  style={{ color: theme.accentColor || "#FFA72A" }}
                >
                  {link.name}
                </NavLink>
              ))}
              <NavLink
                to="/login"
                className={({ isActive }) =>
                  isActive
                    ? "font-bold text-[120%] hover:opacity-90 focus-visible:opacity-90"
                    : "text-[120%] hover:opacity-90 focus:opacity-90"
                }
                style={{ color: theme.accentColor || "#FFA72A" }}
              >
                Login
              </NavLink>
              <NavLink
                to="/get-started"
                style={{
                  backgroundColor: theme.accentColor || "#FFA72A",
                  color: theme.buttonTextColor || "#fff",
                  borderRadius: "50px",
                  padding: "6px 20px",
                  fontWeight: 600,
                  fontSize: "1rem",
                  textDecoration: "none",
                  whiteSpace: "nowrap",
                  transition: "opacity 0.2s ease",
                }}
                onMouseEnter={(e) => (e.currentTarget.style.opacity = "0.88")}
                onMouseLeave={(e) => (e.currentTarget.style.opacity = "1")}
              >
                Start Free
              </NavLink>
            </>
          )}
        </nav>
      </div>

      {/* Mobile Menu */}
      {isOpen && (
        <nav
          className={`${showHamburger ? "block" : "hidden"} mt-4 max-h-[calc(100vh-80px)] overflow-y-auto`}
        >
          {shouldShowMenuItems ? (
            <>
              {menuLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  onClick={handleMenuItemClick}
                  className="block py-2 px-4 text-[120%]"
                  style={{
                    color: theme.accentColor || "#FFA72A",
                  }}
                >
                  {link.name}
                </NavLink>
              ))}
              <NavLink
                to="#"
                onClick={handleLogout}
                className="block py-2 px-4 text-[120%]"
                style={{
                  color: theme.accentColor || "#FFA72A",
                }}
              >
                Logout
              </NavLink>
            </>
          ) : (
            <>
              {publicMenuLinks.map((link) => (
                <NavLink
                  key={link.path}
                  to={link.path}
                  onClick={handleMenuItemClick}
                  className="block py-2 px-4 text-[120%]"
                  style={{ color: theme.accentColor || "#FFA72A" }}
                >
                  {link.name}
                </NavLink>
              ))}
              <NavLink
                to="/login"
                onClick={handleMenuItemClick}
                className="block py-2 px-4 text-[120%]"
                style={{ color: theme.accentColor || "#FFA72A" }}
              >
                Login
              </NavLink>
              <NavLink
                to="/get-started"
                onClick={handleMenuItemClick}
                className="block py-2 px-4 text-[120%] font-semibold"
                style={{ color: theme.accentColor || "#FFA72A" }}
              >
                Start Free
              </NavLink>
            </>
          )}
        </nav>
      )}
    </header>
  );
};

export default withTheme(Header);
